import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { staffAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  Clock, 
  LogIn, 
  LogOut, 
  Coffee, 
  User,
  Calendar,
  Timer,
  RefreshCw
} from 'lucide-react';

export default function StaffPresenceSection() {
  const [presenceRecords, setPresenceRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaff, selectedDate]);

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaff, startDate, endDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const [staffRes, presenceRes] = await Promise.all([
        staffAPI.list().catch(() => ({ data: [] })),
        staffAPI.getPresence(staffId || undefined, selectedDate || undefined).catch(() => ({ data: [] }))
      ]);
      
      setStaff(staffRes.data || []);
      setPresenceRecords(presenceRes.data || []);
    } catch (error) {
      console.error('Errore caricamento presenze:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const res = await staffAPI.getPresenceSummary(
        staffId || undefined,
        startDate,
        endDate
      );
      setSummary(res.data || []);
    } catch (error) {
      console.error('Errore caricamento riepilogo:', error);
    }
  };

  const handleRecordPresence = async (type) => {
    try {
      await staffAPI.recordPresence(type, null);
      toast.success(type === 'CLOCK_IN' ? 'Entrata registrata' : 'Uscita registrata');
      loadData();
      loadSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore registrazione');
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'CLOCK_IN': return 'Entrata';
      case 'CLOCK_OUT': return 'Uscita';
      case 'BREAK_START': return 'Inizio Pausa';
      case 'BREAK_END': return 'Fine Pausa';
      default: return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'CLOCK_IN': return 'bg-green-500';
      case 'CLOCK_OUT': return 'bg-red-500';
      case 'BREAK_START': return 'bg-yellow-500';
      case 'BREAK_END': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'CLOCK_IN': return <LogIn className="w-4 h-4" />;
      case 'CLOCK_OUT': return <LogOut className="w-4 h-4" />;
      case 'BREAK_START': return <Coffee className="w-4 h-4" />;
      case 'BREAK_END': return <Coffee className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Quick Actions for Staff */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Timbra Presenza
          </CardTitle>
          <CardDescription>
            Registra la tua entrata o uscita
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => handleRecordPresence('CLOCK_IN')}
              className="bg-green-600 hover:bg-green-700"
              data-testid="clock-in-btn"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Entrata
            </Button>
            <Button 
              onClick={() => handleRecordPresence('CLOCK_OUT')}
              variant="destructive"
              data-testid="clock-out-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Uscita
            </Button>
            <Button 
              onClick={() => handleRecordPresence('BREAK_START')}
              variant="outline"
              data-testid="break-start-btn"
            >
              <Coffee className="w-4 h-4 mr-2" />
              Inizio Pausa
            </Button>
            <Button 
              onClick={() => handleRecordPresence('BREAK_END')}
              variant="outline"
              data-testid="break-end-btn"
            >
              <Coffee className="w-4 h-4 mr-2" />
              Fine Pausa
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Registro Presenze
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div>
              <Label>Operatore</Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger data-testid="staff-filter">
                  <SelectValue placeholder="Tutti gli operatori" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {staff.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.user?.name || 'Operatore'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data</Label>
              <Input 
                type="date" 
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                data-testid="date-filter"
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={loadData} className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Aggiorna
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : presenceRecords.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nessuna registrazione per questa data
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Ora</th>
                    <th>Operatore</th>
                    <th>Tipo</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {presenceRecords.map((record, idx) => (
                    <tr key={record.id || idx}>
                      <td>{formatDate(record.timestamp)}</td>
                      <td>{formatTime(record.timestamp)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          {record.staff_name || 'Operatore'}
                        </div>
                      </td>
                      <td>
                        <Badge className={`${getTypeColor(record.type)} text-white`}>
                          <span className="flex items-center gap-1">
                            {getTypeIcon(record.type)}
                            {getTypeLabel(record.type)}
                          </span>
                        </Badge>
                      </td>
                      <td className="text-muted-foreground">
                        {record.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="w-5 h-5" />
            Riepilogo Ore Lavorate
          </CardTitle>
          <CardDescription>
            Seleziona il periodo per visualizzare il riepilogo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div>
              <Label>Da</Label>
              <Input 
                type="date" 
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-filter"
              />
            </div>
            <div>
              <Label>A</Label>
              <Input 
                type="date" 
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-filter"
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={loadSummary} className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Calcola
              </Button>
            </div>
          </div>

          {summary.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nessun dato per il periodo selezionato
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {summary.map((item, idx) => (
                <Card key={item.staff_id || idx} className="bg-muted/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{item.staff_name}</p>
                        <p className="text-sm text-muted-foreground">Operatore</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-background rounded p-2">
                        <p className="text-muted-foreground">Ore totali</p>
                        <p className="text-lg font-bold text-primary">{item.total_hours}h</p>
                      </div>
                      <div className="bg-background rounded p-2">
                        <p className="text-muted-foreground">Minuti</p>
                        <p className="text-lg font-bold">{item.total_minutes}m</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
